import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import Deck from './Deck';
import { PartOne, PartTwo } from './Chapters';
import Reliability from './Reliability';
import TrialGate from './TrialGate';
import GitLab, { RELEASES_URL } from './GitLab';
import HmiView from './HmiView';
import HowItWorks from './HowItWorks';
import QuestionTypes from './QuestionTypes';
import WhyCrop from './WhyCrop';
import { PageCut, PageStore } from './PageLibrary';
import PageTree from './PageTree';
import { TARGET, pad } from './screenData';
import translations from './translations';
import { BLUE, EASE, REPO_URL, Segmented, readPref, writePref } from './ui';
import styles from './ScreenInspector.module.css';

const LANGS = [{ id: 'en', label: 'EN' }, { id: 'zh', label: '中文' }];

// The hero's highlight box walks over a few components, pausing on each.
const STOPS = ['overview.pressure_value', 'header.status', 'overview.circulation_pump', 'overview.winding_a']
    .map((n) => pad(TARGET[n].box, 3));
const WALK = [...STOPS.flatMap((b) => [b, b]), STOPS[0]];

function Hero({ t, next }) {
    return (
        <div className={styles.hero}>
            <motion.div
                className={styles.heroText}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE }}
            >
                <div className={styles.heroLead}>
                    <p className={styles.heroName}>{t.hero.name}</p>
                    <h1 className={styles.heroTitle}>{t.hero.title}</h1>
                </div>
                <div className={styles.heroAside}>
                    <p className={styles.heroSub}>{t.hero.sub}</p>
                    <div className={styles.heroLinks}>
                        <button type="button" className={styles.link} onClick={next}>
                            {t.hero.link}
                            <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
                        </button>
                        <a href={REPO_URL} target="_blank" rel="noreferrer" className={styles.link}>
                            {t.hero.code}
                            <OpenInNewRoundedIcon sx={{ fontSize: 15, ml: 0.5 }} />
                        </a>
                        <a href={RELEASES_URL} target="_blank" rel="noreferrer" className={styles.link}>
                            {t.hero.releases}
                            <OpenInNewRoundedIcon sx={{ fontSize: 15, ml: 0.5 }} />
                        </a>
                    </div>
                </div>
            </motion.div>

            <motion.div
                className={styles.heroVisual}
                initial={{ opacity: 0, y: 48, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.2, ease: EASE }}
            >
                <div className={`${styles.frame} ${styles.heroFrame}`}>
                    <HmiView state="running" label="Mock compressor HMI">
                        <motion.rect
                            initial={false}
                            animate={{
                                attrX: WALK.map((b) => b[0]),
                                attrY: WALK.map((b) => b[1]),
                                width: WALK.map((b) => b[2]),
                                height: WALK.map((b) => b[3]),
                            }}
                            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                            rx={6}
                            fill="rgba(56,189,248,0.10)"
                            stroke={BLUE}
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                        />
                    </HmiView>
                </div>
                <p className={styles.caption}>{t.hero.caption}</p>
            </motion.div>
        </div>
    );
}

export default function ScreenInspector() {
    const [lang, setLangState] = useState(() => readPref(
        'screeninspector.lang',
        (navigator.language || '').toLowerCase().startsWith('zh') ? 'zh' : 'en',
        ['en', 'zh'],
    ));
    const setLang = (v) => {
        setLangState(v);
        writePref('screeninspector.lang', v);
    };
    const t = translations[lang];

    useEffect(() => {
        const before = document.title;
        document.title = 'Screen Inspector';
        return () => { document.title = before; };
    }, []);

    const slides = useMemo(() => {
        const L = t.deck.labels;
        // Part 1 (slides 2–6) is the library tests import; part 2 (slides 7–11) is the
        // MCP server, where an AI drives that library to build entries and page methods.
        // The last slide shows the GitLab project and its latest release.
        return [
            { id: 'intro', label: L[0], render: ({ next }) => <Hero t={t} next={next} /> },
            { id: 'part1', label: L[1], tone: 'gray', render: () => <PartOne t={t} /> },
            {
                id: 'how', label: L[2], builds: t.how.steps.length,
                render: ({ build, setBuild }) => <HowItWorks t={t} step={build} onStep={setBuild} />,
            },
            { id: 'types', label: L[3], tone: 'gray', render: () => <QuestionTypes t={t} lang={lang} /> },
            { id: 'crop', label: L[4], render: () => <WhyCrop t={t} /> },
            { id: 'reliability', label: L[5], tone: 'dark', render: () => <Reliability t={t} /> },
            { id: 'part2', label: L[6], tone: 'gray', render: () => <PartTwo t={t} /> },
            { id: 'cut', label: L[7], render: () => <PageCut t={t} /> },
            { id: 'store', label: L[8], tone: 'gray', render: () => <PageStore t={t} /> },
            { id: 'tree', label: L[9], render: () => <PageTree t={t} /> },
            { id: 'gate', label: L[10], tone: 'gray', render: () => <TrialGate t={t} lang={lang} /> },
            { id: 'code', label: L[11], render: () => <GitLab t={t} lang={lang} /> },
        ];
    }, [t, lang]);

    return (
        <div className={styles.page} lang={lang === 'zh' ? 'zh-CN' : 'en'}>
            <Deck
                slides={slides}
                prevLabel={t.deck.prev}
                nextLabel={t.deck.next}
                corner={<Segmented small group="lang" label={t.language} options={LANGS} value={lang} onChange={setLang} />}
            />
        </div>
    );
}
